import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listBotVersions, deleteBotVersion, getBotVersion, createBotVersion, listBacktestRuns, listLiveRuns, stopLiveRun } from '../../api/botsApi';
import { fetchAccounts } from '../../api/api';
import PageLayout from '../PageLayout';
import { Button } from '../ui/button';
import LaunchLiveRunModal from './LaunchLiveRunModal';
import LaunchBacktestModal from './LaunchBacktestModal';
import { Rocket, Pencil, Eye, PlayCircle, Trash2, Copy, StopCircle } from 'lucide-react';
import StatusBadge from './StatusBadge';

export default function BotVersionsPage({ botId }) {
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBacktestModal, setShowBacktestModal] = useState(false);
  const [lastRuns, setLastRuns] = useState({}); // { [versionId]: run }
  const [lastLiveRuns, setLastLiveRuns] = useState({}); // { [versionId]: liveRun }
  const [stoppingVersionId, setStoppingVersionId] = useState(null);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listBotVersions(botId);
        setVersions(Array.isArray(res?.results) ? res.results : res);
      } catch (e) {
        console.error('Failed to load versions', e);
        setError(e.message || 'Failed to load versions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [botId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const accs = await fetchAccounts();
        if (!mounted) return;
        setAccounts(Array.isArray(accs?.results) ? accs.results : accs || []);
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  // After versions load, fetch the latest backtest run for each version
  useEffect(() => {
    if (!versions || versions.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const pairs = await Promise.all(
          versions.map(async (v) => {
            try {
              const data = await listBacktestRuns({ bot_version_id: v.id });
              const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
              const sorted = [...list].sort((a, b) => {
                const ta = new Date(a.completed_at || a.created_at || 0).getTime();
                const tb = new Date(b.completed_at || b.created_at || 0).getTime();
                return tb - ta; // desc
              });
              const last = sorted[0] || null;
              return [v.id, last];
            } catch (e) {
              return [v.id, null];
            }
          })
        );
        if (cancelled) return;
        const map = {};
        pairs.forEach(([id, run]) => { map[id] = run; });
        setLastRuns(map);
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [versions]);

  // Fetch latest live run per version
  useEffect(() => {
    if (!versions || versions.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const pairs = await Promise.all(
          versions.map(async (v) => {
            try {
              const data = await listLiveRuns({ bot_version_id: v.id });
              const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
              const sorted = [...list].sort((a, b) => {
                const ta = new Date(a.started_at || a.created_at || 0).getTime();
                const tb = new Date(b.started_at || b.created_at || 0).getTime();
                return tb - ta; // desc
              });
              const last = sorted[0] || null;
              return [v.id, last];
            } catch (e) {
              return [v.id, null];
            }
          })
        );
        if (cancelled) return;
        const map = {};
        pairs.forEach(([id, run]) => { map[id] = run; });
        setLastLiveRuns(map);
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [versions]);

  const refreshVersions = async () => {
    try {
      const res = await listBotVersions(botId);
      setVersions(Array.isArray(res?.results) ? res.results : res);
    } catch (e) {
      console.error('Failed to refresh versions', e);
    }
  };

  const openLiveRunModal = (versionId) => {
    setSelectedVersionId(versionId);
    setShowLiveModal(true);
  };

  const openBacktestModal = (versionId) => {
    setSelectedVersionId(versionId);
    setShowBacktestModal(true);
  };

  const handleLiveStarted = () => {
    setShowLiveModal(false);
  };

  const handleDelete = async (versionId) => {
    if (!window.confirm('Delete this version? This cannot be undone.')) return;
    try {
      await deleteBotVersion(versionId);
      await refreshVersions();
    } catch (e) {
      console.error('Failed to delete version', e);
      alert(e?.message || 'Failed to delete version.');
    }
  };

  const handleStopLiveRun = async (versionId) => {
    const lr = lastLiveRuns[versionId];
    if (!lr || !lr.id) return;
    if (!window.confirm('Stop the running live run?')) return;
    try {
      setStoppingVersionId(versionId);
      await stopLiveRun(lr.id);
      // Refresh the latest live run for just this version
      try {
        const data = await listLiveRuns({ bot_version_id: versionId });
        const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        const sorted = [...list].sort((a, b) => {
          const ta = new Date(a.started_at || a.created_at || 0).getTime();
          const tb = new Date(b.started_at || b.created_at || 0).getTime();
          return tb - ta;
        });
        const last = sorted[0] || null;
        setLastLiveRuns((prev) => ({ ...prev, [versionId]: last }));
      } catch (_) {
        // ignore
      }
    } catch (e) {
      console.error('Failed to stop live run', e);
      alert(e?.message || 'Failed to stop live run.');
    } finally {
      setStoppingVersionId(null);
    }
  };

  const handleDuplicate = async (versionId) => {
    const newName = window.prompt('Enter a name for the duplicated version:');
    if (!newName) return;
    try {
      const v = await getBotVersion(versionId);
      const payload = {
        version_name: newName,
        strategy_name: v.strategy_name,
        strategy_params: v.strategy_params,
        indicator_configs: v.indicator_configs,
        notes: v.notes,
      };
      await createBotVersion(botId, payload);
      await refreshVersions();
    } catch (e) {
      console.error('Failed to duplicate version', e);
      alert(e?.message || 'Failed to duplicate version.');
    }
  };

  const backButton = {
    label: 'Back to Bot',
    to: `/bots/${botId}`,
    preferHistory: false,
    hideLabelOnMobile: true,
  };

  if (loading) {
    return (
      <PageLayout title="Bot Versions" backButton={backButton}>
        <div className="p-4">Loading versions…</div>
      </PageLayout>
    );
  }
  if (error) {
    return (
      <PageLayout title="Bot Versions" backButton={backButton}>
        <div className="p-4 text-destructive">{error}</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Bot Versions" backButton={backButton}>
      <div className="space-y-6">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {versions.map((v) => (
            <div key={v.id} className="border border-borderDefault rounded-md p-4 bg-backgroundSurface">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                {/* Left side: Name + Created at */}
                <div>
                  <div className="font-semibold text-textPrimary">{v.version_name || v.id?.slice(0, 8)}</div>
                  <div className="text-xs text-textSecondary mt-1">Created: {v.created_at ? new Date(v.created_at).toLocaleString() : '-'}</div>
                  <div className="text-xs text-textSecondary mt-1">
                    {(() => {
                      const r = lastRuns[v.id];
                      if (!r) return <>Last Backtest: -</>;
                      const dt = new Date(r.completed_at || r.created_at).toLocaleString();
                      let pnlVal = undefined;
                      if (r?.stats?.net_pnl !== undefined && r?.stats?.net_pnl !== null) {
                        const num = typeof r.stats.net_pnl === 'number' ? r.stats.net_pnl : parseFloat(r.stats.net_pnl);
                        pnlVal = isNaN(num) ? undefined : num;
                      }
                      const pnlClass = pnlVal === undefined ? '' : (pnlVal >= 0 ? 'text-green-600' : 'text-red-600');
                      return (
                        <>
                          Last Backtest: {dt} - <span className={pnlClass}>{pnlVal !== undefined ? pnlVal.toFixed(2) : '-'}</span>
                        </>
                      );
                    })()}
                  </div>
                  <div className="text-xs text-textSecondary mt-1">
                    {(() => {
                      const lr = lastLiveRuns[v.id];
                      if (!lr) return <>Last Live Run: -</>;
                      const dt = (lr.started_at || lr.created_at) ? new Date(lr.started_at || lr.created_at).toLocaleString() : '-';
                      const accMap = (accounts || []).reduce((m, a) => { m[a.id] = `${a.name} (${a.platform})`; return m; }, {});
                      const acct = accMap[lr.account_id] || lr.account_id || '-';
                      const instrument = lr.instrument_symbol || lr.symbol || '-';
                      return (
                        <>
                          Last Live Run: {dt} - <StatusBadge status={lr.status || '-'} />
                          <div className="mt-1 text-xs text-textSecondary">Instrument: <span className="font-medium">{instrument}</span> · Account: <span className="font-medium">{acct}</span></div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {/* Right side: Vertical actions */}
                <div className="flex flex-col gap-2">
                  <Button variant="primary" className="justify-start" onClick={() => openBacktestModal(v.id)}>
                    <Rocket className="w-4 h-4 mr-2" /> Launch Backtest
                  </Button>
                  <Link to={`/bots/${botId}/strategy-builder/${v.id}`} className="w-full">
                    <Button variant="outline" className="w-full justify-start">
                      <Pencil className="w-4 h-4 mr-2" /> Edit Strategy
                    </Button>
                  </Link>
                  <Link to={`/bots/${botId}/versions/${v.id}`} className="w-full">
                    <Button variant="outline" className="w-full justify-start">
                      <Eye className="w-4 h-4 mr-2" /> View Backtest
                    </Button>
                  </Link>
                  {(() => {
                    const status = lastLiveRuns[v.id]?.status;
                    const normalized = String(status || '').trim().toUpperCase();
                    const isRunning = ['RUNNING', 'ACTIVE', 'IN_PROGRESS', 'LIVE'].includes(normalized);
                    if (isRunning) {
                      return (
                        <Button
                          variant="destructive"
                          className="justify-start"
                          onClick={() => handleStopLiveRun(v.id)}
                          disabled={stoppingVersionId === v.id}
                        >
                          <StopCircle className="w-4 h-4 mr-2" /> {stoppingVersionId === v.id ? 'Stopping…' : 'Stop Live Run'}
                        </Button>
                      );
                    }
                    return (
                      <Button variant="secondary" className="justify-start" onClick={() => openLiveRunModal(v.id)}>
                        <PlayCircle className="w-4 h-4 mr-2" /> Start Live Run
                      </Button>
                    );
                  })()}
                  <div className="mt-2 flex items-center gap-2">
                    <Button variant="ghost" size="sm" title="Duplicate" onClick={() => handleDuplicate(v.id)} className="p-2">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" title="Delete" onClick={() => handleDelete(v.id)} className="p-2 text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      {showLiveModal && (
        <LaunchLiveRunModal
          botVersionId={selectedVersionId}
          onSuccess={handleLiveStarted}
          onClose={() => setShowLiveModal(false)}
        />
      )}
      {showBacktestModal && (
        <LaunchBacktestModal
          botVersionId={selectedVersionId}
          onSuccess={() => setShowBacktestModal(false)}
          onClose={() => setShowBacktestModal(false)}
        />
      )}
      </div>
    </PageLayout>
  );
}
